import {
  ProductChoice,
  SelectedManualAction,
  SelectedPartAction,
  SelectedProposalPackage,
  ServiceProposalManualAction,
  ServiceProposalPackage,
  ServiceProposalPartAction,
} from "@repo/common"

export function createDefaultProductDecisions(
  productChoices: ProductChoice[],
): Record<string, string> {
  const decisions: Record<string, string> = {}
  for (const choice of productChoices) {
    decisions[choice.id] = choice.recommendedAlternative.id
  }
  return decisions
}

export function updatePartActionWithSelectedProducts(
  partActionData: ServiceProposalPartAction,
): ServiceProposalPartAction {
  // Note: The new ProductChoice structure doesn't have selectedProductId
  // The selection is tracked separately in selectedAction.productDecisions
  // This function now just returns the partActionData as-is since
  // product selection is handled by the productDecisions array
  return partActionData
}

export function addPartAction(
  currentValue: SelectedPartAction[],
  partId: string,
  productChoices: ProductChoice[],
): SelectedPartAction[] {
  return [
    ...currentValue,
    {
      partId,
      productDecisions: createDefaultProductDecisions(productChoices),
    },
  ]
}

export function removePartAction(
  currentValue: SelectedPartAction[],
  partId: string,
): SelectedPartAction[] {
  return currentValue.filter((spa) => spa.partId !== partId)
}

export function updateProductDecision(
  currentValue: SelectedPartAction[],
  partId: string,
  choiceId: string,
  productId: string,
): SelectedPartAction[] {
  return currentValue.map((spa) =>
    spa.partId === partId
      ? {
          ...spa,
          productDecisions: {
            ...spa.productDecisions,
            [choiceId]: productId,
          },
        }
      : spa,
  )
}

// Manual Action utilities
export function updateManualActionWithSelectedProducts(
  manualActionData: ServiceProposalManualAction,
): ServiceProposalManualAction {
  // Note: The new ProductChoice structure doesn't have selectedProductId
  // The selection is tracked separately in selectedAction.productDecisions
  // This function now just returns the manualActionData as-is since
  // product selection is handled by the productDecisions array
  return manualActionData
}

export function addManualAction(
  currentValue: SelectedManualAction[],
  actionId: string,
  productChoices: ProductChoice[],
): SelectedManualAction[] {
  return [
    ...currentValue,
    {
      id: actionId,
      productDecisions: createDefaultProductDecisions(productChoices),
    },
  ]
}

export function removeManualAction(
  currentValue: SelectedManualAction[],
  actionId: string,
): SelectedManualAction[] {
  return currentValue.filter((sma) => sma.id !== actionId)
}

export function updateManualActionProductDecision(
  currentValue: SelectedManualAction[],
  actionId: string,
  choiceId: string,
  productId: string,
): SelectedManualAction[] {
  return currentValue.map((sma) =>
    sma.id === actionId
      ? {
          ...sma,
          productDecisions: {
            ...sma.productDecisions,
            [choiceId]: productId,
          },
        }
      : sma,
  )
}

// Package utilities
export function createSelectedPackage(
  packageData: ServiceProposalPackage,
): SelectedProposalPackage {
  return {
    originalPackageId: packageData.originalPackageId,
    selectedPartActions: packageData.partActions.map((pa) => ({
      partId: pa.partId,
      productDecisions: createDefaultProductDecisions(pa.productChoices),
    })),
    selectedWashOriginalId: packageData.wash?.originalWashId ?? undefined,
  }
}

export function addPackage(
  currentValue: SelectedProposalPackage[],
  packageData: ServiceProposalPackage,
): SelectedProposalPackage[] {
  return [...currentValue, createSelectedPackage(packageData)]
}

export function removePackage(
  currentValue: SelectedProposalPackage[],
  originalPackageId: string,
): SelectedProposalPackage[] {
  return currentValue.filter((sp) => sp.originalPackageId !== originalPackageId)
}

// Package Part Action utilities
interface PackageWithSelectedPartActions {
  originalPackageId: string
  selectedPartActions: SelectedPartAction[]
  selectedWashOriginalId?: string | undefined
}

export function updatePackagePartActionsWithSelectedProducts(
  packagePartActions: ServiceProposalPartAction[],
): ServiceProposalPartAction[] {
  // Note: The new ProductChoice structure doesn't have selectedProductId property
  // Product selections are tracked separately in the productDecisions structure
  // This function now just returns the packagePartActions as-is since
  // product selection is handled by the productDecisions array
  return packagePartActions
}

export function getPackagePartActionSelection(
  packagePartActions: ServiceProposalPartAction[],
  selectedPartActions: SelectedPartAction[],
) {
  const selectedPartIds = selectedPartActions.map((pa) => pa.partId)
  const removedPartIds = packagePartActions
    .map((pa) => pa.partId)
    .filter((partId) => !selectedPartIds.includes(partId))

  return { selectedPartIds, removedPartIds }
}

export function togglePackagePartAction(
  currentPackage: PackageWithSelectedPartActions,
  partAction: ServiceProposalPartAction,
  isRemoved: boolean,
): PackageWithSelectedPartActions {
  const newSelectedPartActions = isRemoved
    ? [
        ...currentPackage.selectedPartActions,
        {
          partId: partAction.partId,
          productDecisions: createDefaultProductDecisions(
            partAction.productChoices,
          ),
        },
      ]
    : currentPackage.selectedPartActions.filter(
        (pa) => pa.partId !== partAction.partId,
      )

  return {
    ...currentPackage,
    selectedPartActions: newSelectedPartActions,
  }
}

export function updatePackagePartActionProductDecision(
  currentPackage: PackageWithSelectedPartActions,
  partId: string,
  choiceId: string,
  productId: string,
): PackageWithSelectedPartActions {
  const updatedSelectedPartActions = currentPackage.selectedPartActions.map(
    (selectedPartAction) => {
      if (selectedPartAction.partId === partId) {
        return {
          ...selectedPartAction,
          productDecisions: {
            ...selectedPartAction.productDecisions,
            [choiceId]: productId,
          },
        }
      }
      return selectedPartAction
    },
  )

  return {
    ...currentPackage,
    selectedPartActions: updatedSelectedPartActions,
  }
}

// Product choices field utilities
interface FormValues {
  selectedPartActions?: SelectedPartAction[]
  selectedManualActions?: SelectedManualAction[]
  selectedPackages?: SelectedProposalPackage[]
}

export function findProductDecision(
  formValues: FormValues,
  actionId: string,
  choiceId: string,
  isManualAction: boolean,
): string | undefined {
  if (isManualAction) {
    const manualAction = formValues.selectedManualActions?.find(
      (sa) => sa.id === actionId,
    )
    return manualAction?.productDecisions[choiceId]
  }

  // Check individual part actions
  const partAction = formValues.selectedPartActions?.find(
    (sa) => sa.partId === actionId,
  )
  if (partAction) {
    return partAction.productDecisions[choiceId]
  }

  // Check package part actions
  for (const selectedPackage of formValues.selectedPackages || []) {
    const packagePartAction = selectedPackage.selectedPartActions?.find(
      (sa) => sa.partId === actionId,
    )
    if (packagePartAction) {
      return packagePartAction.productDecisions[choiceId]
    }
  }

  return undefined
}

export function isActionSelected(
  formValues: FormValues,
  actionId: string,
  isManualAction: boolean,
): boolean {
  if (isManualAction) {
    return (formValues.selectedManualActions || []).some(
      (sa) => sa.id === actionId,
    )
  }

  // Check individual part actions
  const isSelectedIndividually = (formValues.selectedPartActions || []).some(
    (sa) => sa.partId === actionId,
  )

  // Check package part actions
  const isSelectedInPackage = (formValues.selectedPackages || []).some((pkg) =>
    pkg.selectedPartActions?.some((sa) => sa.partId === actionId),
  )

  return isSelectedIndividually || isSelectedInPackage
}

export function isActionInSelectedPackage(
  formValues: FormValues,
  actionId: string,
): boolean {
  return (formValues.selectedPackages || []).some((pkg) =>
    pkg.selectedPartActions?.some((sa) => sa.partId === actionId),
  )
}

// Function to update product decision in the correct form field
export function updateProductDecisionInForm(
  formValues: FormValues,
  updateField: {
    selectedPartActions: (
      updater: (prev: SelectedPartAction[]) => SelectedPartAction[],
    ) => void
    selectedManualActions: (
      updater: (prev: SelectedManualAction[]) => SelectedManualAction[],
    ) => void
    selectedPackages: (
      updater: (prev: SelectedProposalPackage[]) => SelectedProposalPackage[],
    ) => void
  },
  actionId: string,
  choiceId: string,
  productId: string,
  isManualAction: boolean,
): void {
  if (isManualAction) {
    updateField.selectedManualActions((prev) =>
      updateManualActionProductDecision(prev, actionId, choiceId, productId),
    )
    return
  }

  if (isActionInSelectedPackage(formValues, actionId)) {
    updateField.selectedPackages((prev) =>
      prev.map((pkg) => {
        const hasAction = pkg.selectedPartActions?.some(
          (sa) => sa.partId === actionId,
        )
        if (hasAction) {
          return updatePackagePartActionProductDecision(
            pkg,
            actionId,
            choiceId,
            productId,
          )
        }
        return pkg
      }),
    )
  } else {
    updateField.selectedPartActions((prev) =>
      updateProductDecision(prev, actionId, choiceId, productId),
    )
  }
}
